// The Clients list. The top bar already titles the page, so it opens with its actions, one row of
// numbers people act on (each one filters the list), and the list itself.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import {
  AlertTriangle, ArchiveRestore, CalendarPlus, ChevronDown, ChevronLeft, ChevronRight, Download, ExternalLink,
  FileSpreadsheet, FileText, FileWarning, Loader2, Mail, MessageSquare, Pencil, Rows, Send, Sparkles, Trash2,
  UserPlus, Users, UsersRound, X,
} from 'lucide-react';
import api from '../../utils/api';
import useConnectedIntegrations from '../../hooks/useConnectedIntegrations';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  BulkBar, CLIENT_TYPES, DataTable, Dialog, EmptyState, ErrorState, Field, OverflowMenu, PAGE_WIDE, PersonAvatar,
  SearchInput, TableSkeleton, Tabs, clientTypeLabel, fmtDate, num, plural,
} from '../../components/ui';
import { ageOf, dayOf, daysUntil, downloadCsv, formatAddress, todayIso, typeSelectValue } from './clientShared';

const TABS = [
  { id: 'active', label: 'Active' },
  { id: 'archived', label: 'Archived' },
  { id: 'teams', label: 'Teams' },
  { id: 'documents', label: 'Expiring documents' },
];
const TAB_IDS = TABS.map((t) => t.id);
const PAGE_SIZE = 25;

// Plan Management, Support Coordination and Aged Care participants live in their own modules — keep
// them out of the general service-delivery list. (Aged care participants are still scheduled: the
// scheduler loads its own client list, untouched by this page-local filter.)
const isOtherModule = (c) => ['plan_managed', 'support_coordination', 'aged_care'].includes(c.service_type);

const SORTERS = {
  name: (c) => `${c.last_name || ''} ${c.first_name || ''}`.trim().toLowerCase(),
  type: (c) => (c.client_type ? clientTypeLabel(c.client_type).toLowerCase() : null),
  ndis: (c) => c.ndis_number || null,
  age: (c) => ageOf(c.date_of_birth),
  review: (c) => dayOf(c.next_review_date) || null,
  contact: (c) => (c.primary_contact_name || '').toLowerCase() || null,
  location: (c) => [c.suburb, c.state].filter(Boolean).join(', ').toLowerCase() || null,
};

const KPI_FILTERS = {
  reviews: { label: 'Reviews due', test: (c) => { const n = daysUntil(c.next_review_date); return n != null && n <= 14; } },
  funds: { label: 'Fund issues', flag: 'Fund low or expired' },
  contacts: { label: 'No contact on file', flag: 'No emergency contact' },
};

const phoneHref = (p) => `tel:${String(p).replace(/[^\d+]/g, '')}`;

// A number that filters the list. Same look as the kit's KpiTile, but it is a real button.
function FilterTile({ label, value, sub, tone = 'slate', active, onClick }) {
  const dot = { slate: 'bg-slate-300', amber: 'bg-amber-500', rose: 'bg-rose-500' }[tone] || 'bg-slate-300';
  return (
    <button type="button" onClick={onClick} aria-pressed={!!active}
      className={clsx('min-w-[11rem] snap-start sm:min-w-0 text-left bg-white border rounded-xl p-4 transition-colors duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400',
        active ? 'border-primary-400 ring-1 ring-primary-200' : 'border-slate-200 hover:border-slate-300')}>
      <span className="flex items-center gap-1.5 text-[12.5px] font-medium text-slate-600">
        <span className={clsx('w-1.5 h-1.5 rounded-full shrink-0', dot)} aria-hidden />{label}
      </span>
      <span className="block font-display text-[26px] font-bold tabular-nums leading-none tracking-[-0.02em] mt-2.5 text-slate-900">{value}</span>
      {sub && <span className="block text-[12px] text-slate-500 mt-2">{sub}</span>}
    </button>
  );
}

function RiskBadge({ rs }) {
  if (!rs || rs.level === 'green') return null;
  const title = rs.flags?.join(' · ');
  return rs.level === 'red'
    ? <span className="badge-red !text-[11px] whitespace-nowrap" title={title}><AlertTriangle size={10} aria-hidden /> High risk</span>
    : <span className="badge-yellow !text-[11px] whitespace-nowrap" title={title}><AlertTriangle size={10} aria-hidden /> At risk</span>;
}

function ReviewCell({ date }) {
  if (!date) return <span className="text-slate-500">—</span>;
  const n = daysUntil(date);
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-slate-600 tabular-nums whitespace-nowrap">
      {fmtDate(dayOf(date))}
      {n != null && n < 0 && <span className="badge-red !text-[11px]">Overdue</span>}
      {n != null && n >= 0 && n <= 7 && <span className="badge-yellow !text-[11px]">{n === 0 ? 'Today' : `In ${n} ${n === 1 ? 'day' : 'days'}`}</span>}
    </span>
  );
}

function ContactCell({ c }) {
  const name = c.primary_contact_name;
  const phone = c.primary_contact_phone;
  if (!name && !phone && !c.mobile) return <span className="text-slate-500">—</span>;
  const label = name || (phone ? 'Contact' : 'Mobile');
  const num_ = phone || (!name ? c.mobile : null);
  return (
    <div className="min-w-0 leading-tight">
      <div className="text-sm text-slate-700 truncate">{label}{c.primary_contact_relation && name ? <span className="text-slate-500"> · {c.primary_contact_relation}</span> : null}</div>
      {num_ && (
        <a href={phoneHref(num_)} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} className="text-[12.5px] text-primary-700 hover:underline tabular-nums">{num_}</a>
      )}
    </div>
  );
}

// The trigger of the Export menu reads like a secondary button.
function ExportTrigger() {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm font-semibold">
      <Download size={15} aria-hidden /><span className="hidden sm:inline">Export</span><ChevronDown size={14} className="hidden sm:inline" aria-hidden />
    </span>
  );
}

export default function ClientList({ archived: routeArchived, teams: routeTeams, expiredDocs: routeDocs }) {
  // Accounting shortcuts show only when that software is connected (Settings › Integrations).
  const connected = useConnectedIntegrations();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [params] = useSearchParams();

  // The tab lives in the address (?tab=), and the /clients/archived, /clients/teams and
  // /clients/documents/expired routes open on their tab.
  const routeTab = routeArchived ? 'archived' : routeTeams ? 'teams' : routeDocs ? 'documents' : null;
  const urlTab = params.get('tab');
  const tab = routeTab || (TAB_IDS.includes(urlTab) ? urlTab : 'active');
  const setTab = (t) => navigate(t === 'active' ? '/clients' : `/clients?tab=${t}`);

  const [clients, setClients] = useState([]);
  const [listState, setListState] = useState({ loading: true, error: null, archivedError: null });
  const [riskScores, setRiskScores] = useState({});
  const [riskError, setRiskError] = useState(false);
  const [docs, setDocs] = useState({ loading: true, error: null, rows: [] });
  const [teams, setTeams] = useState({ loading: false, error: null, rows: null });
  const [unpaid, setUnpaid] = useState(null);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [kpi, setKpi] = useState(null);
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const [page, setPage] = useState(1);
  const [dense, setDense] = useState(false);
  const [selected, setSelected] = useState([]);

  const [scan, setScan] = useState({ open: false, loading: false, alerts: null });
  const [xlsx, setXlsx] = useState({ open: false, password: '', error: null, busy: false });

  // ── Loading ────────────────────────────────────────────────────────────────
  const loadClients = useCallback(async () => {
    setListState((s) => ({ ...s, loading: true, error: null }));
    const [act, arch, rs] = await Promise.allSettled([
      api.get('/clients?limit=2000&with_contact=1'),
      api.get('/clients?limit=2000&is_active=false&with_contact=1'),
      api.get('/clients/risk-scores'),
    ]);
    if (act.status === 'rejected') {
      setListState({ loading: false, error: act.reason?.response?.data?.message || 'We couldn’t load your clients. Check your connection, then try again.', archivedError: null });
      return;
    }
    const pick = (r) => (Array.isArray(r.value.data) ? r.value.data : (r.value.data?.clients || []));
    const active = pick(act);
    const archived = arch.status === 'fulfilled' ? pick(arch) : [];
    setClients([...active, ...archived].filter((c) => !isOtherModule(c)));
    setRiskScores(rs.status === 'fulfilled' ? (rs.value.data || {}) : {});
    setRiskError(rs.status === 'rejected');
    setListState({ loading: false, error: null, archivedError: arch.status === 'rejected' ? 'We couldn’t load archived clients.' : null });
  }, []);

  const loadDocs = useCallback(() => {
    setDocs((d) => ({ ...d, loading: true, error: null }));
    api.get('/clients/documents/expiring?days=30')
      .then((r) => setDocs({ loading: false, error: null, rows: Array.isArray(r.data) ? r.data : [] }))
      .catch((e) => setDocs({ loading: false, error: e.response?.data?.message || 'We couldn’t load expiring documents.', rows: [] }));
  }, []);

  const loadTeams = useCallback(() => {
    setTeams({ loading: true, error: null, rows: null });
    api.get('/teams')
      .then((r) => setTeams({ loading: false, error: null, rows: r.data?.teams || (Array.isArray(r.data) ? r.data : []) }))
      .catch((e) => setTeams({ loading: false, error: e.response?.data?.message || 'We couldn’t load teams.', rows: null }));
  }, []);

  useEffect(() => { loadClients(); loadDocs(); }, [loadClients, loadDocs]);
  useEffect(() => {
    api.get('/clients/stats').then((r) => setUnpaid(Number(r.data?.unpaidInvoices) || 0)).catch(() => setUnpaid(null));
  }, []);
  useEffect(() => { if (tab === 'teams' && !teams.rows && !teams.loading && !teams.error) loadTeams(); }, [tab, teams, loadTeams]);
  // A new view starts at the top of the list with nothing selected.
  useEffect(() => { setPage(1); setSelected([]); }, [tab, search, typeFilter, kpi, sort]);

  // ── Numbers ────────────────────────────────────────────────────────────────
  const activeClients = useMemo(() => clients.filter((c) => c.is_active), [clients]);
  const archivedClients = useMemo(() => clients.filter((c) => !c.is_active), [clients]);
  const flagged = useCallback((c, flag) => (riskScores[c.id]?.flags || []).includes(flag), [riskScores]);
  const kpiCount = (key) => {
    const f = KPI_FILTERS[key];
    return activeClients.filter((c) => (f.test ? f.test(c) : flagged(c, f.flag))).length;
  };
  const reviewsDue = kpiCount('reviews');
  const reviewsOverdue = activeClients.filter((c) => { const n = daysUntil(c.next_review_date); return n != null && n < 0; }).length;
  const fundIssues = kpiCount('funds');
  const noContact = kpiCount('contacts');
  const activeIds = useMemo(() => new Set(activeClients.map((c) => c.id)), [activeClients]);
  const docRows = useMemo(() => docs.rows.filter((d) => activeIds.has(d.client_id)), [docs.rows, activeIds]);
  const docsExpired = docRows.filter((d) => { const n = daysUntil(d.expiry_date); return n != null && n < 0; }).length;

  // ── The list ───────────────────────────────────────────────────────────────
  const base = tab === 'archived' ? archivedClients : activeClients;
  const typeCounts = useMemo(() => base.reduce((m, c) => {
    const v = typeSelectValue(c.client_type);
    if (v) m[v] = (m[v] || 0) + 1;
    return m;
  }, {}), [base]);
  const typeOptions = useMemo(() => {
    const vals = CLIENT_TYPES.map((o) => o.value);
    Object.keys(typeCounts).forEach((v) => { if (!vals.includes(v)) vals.push(v); });
    return vals.map((v) => ({ value: v, label: `${clientTypeLabel(v)} (${typeCounts[v] || 0})` }));
  }, [typeCounts]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const f = tab === 'active' && kpi ? KPI_FILTERS[kpi] : null;
    const rows = base.filter((c) => {
      if (q) {
        const hay = [c.first_name, c.last_name, `${c.first_name || ''} ${c.last_name || ''}`, c.ndis_number,
          c.primary_contact_name, c.primary_contact_phone, c.mobile, c.suburb].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (typeFilter !== 'all' && typeSelectValue(c.client_type) !== typeFilter) return false;
      if (f && !(f.test ? f.test(c) : flagged(c, f.flag))) return false;
      return true;
    });
    const read = SORTERS[sort.key] || SORTERS.name;
    const dir = sort.dir === 'desc' ? -1 : 1;
    return rows.sort((a, b) => {
      const x = read(a); const y = read(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir;
      return String(x).localeCompare(String(y), 'en-AU', { numeric: true, sensitivity: 'base' }) * dir;
    });
  }, [base, search, typeFilter, kpi, tab, sort, flagged]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pages);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const from = filtered.length ? (safePage - 1) * PAGE_SIZE + 1 : 0;
  const to = Math.min(safePage * PAGE_SIZE, filtered.length);
  const filtersOn = !!(search.trim() || typeFilter !== 'all' || (tab === 'active' && kpi));
  const clearFilters = () => { setSearch(''); setTypeFilter('all'); setKpi(null); };

  const pickKpi = (key) => {
    if (tab !== 'active') setTab('active');
    setKpi((k) => (k === key ? null : key));
  };

  // ── Actions ────────────────────────────────────────────────────────────────
  const nameOf = (c) => `${c.first_name || ''} ${c.last_name || ''}`.trim();

  async function restoreClient(c) {
    const name = nameOf(c);
    if (!await confirm(`${name} goes back on the active clients list.`, { title: `Restore ${name}?`, confirmLabel: 'Restore client' })) return;
    try {
      await api.patch(`/clients/${c.id}/restore`);
      setClients((prev) => prev.map((x) => (x.id === c.id ? { ...x, is_active: true } : x)));
      toast.success(`${name} restored to active`);
      setTab('active');
    } catch (err) { toast.error(err.response?.data?.message || 'Could not restore this client'); }
  }

  async function permanentDelete(c) {
    const name = nameOf(c);
    if (!await confirm(`This removes ${name} and all of their records: shifts, notes, documents, funds and family-portal access. It can't be undone.`, {
      title: `Permanently delete ${name}?`, danger: true, confirmLabel: 'Delete permanently',
    })) return;
    try {
      await api.delete(`/clients/${c.id}/permanent`);
      setClients((prev) => prev.filter((x) => x.id !== c.id));
      toast.success(`${name} permanently deleted`);
    } catch (err) { toast.error(err.response?.data?.message || 'Could not permanently delete this client'); }
  }

  const rowMenu = (c) => (tab === 'archived' ? [
    { label: 'Open record', icon: ExternalLink, onClick: () => navigate(`/clients/${c.id}`) },
    { label: 'Restore client', icon: ArchiveRestore, onClick: () => restoreClient(c) },
    { divider: true },
    { label: 'Delete permanently', icon: Trash2, danger: true, onClick: () => permanentDelete(c) },
  ] : [
    { label: 'Open record', icon: ExternalLink, onClick: () => navigate(`/clients/${c.id}`) },
    { label: 'Edit details', icon: Pencil, onClick: () => navigate(`/clients/${c.id}/edit`) },
    { label: 'Schedule shift', icon: CalendarPlus, onClick: () => navigate(`/scheduler?clientId=${c.id}`) },
  ]);

  const csvRows = (rows) => [
    ['First name', 'Last name', 'NDIS number', 'Plan management', 'Date of birth', 'Next review', 'Primary contact',
      'Contact phone', 'Contact email', 'Mobile', 'Address', 'Suburb', 'State', 'Postcode'],
    ...rows.map((c) => [c.first_name, c.last_name, c.ndis_number, c.client_type ? clientTypeLabel(c.client_type) : '',
      dayOf(c.date_of_birth), dayOf(c.next_review_date), c.primary_contact_name, c.primary_contact_phone,
      c.primary_contact_email, c.mobile, formatAddress(c), c.suburb, c.state, c.postcode]),
  ];

  const exportCsv = (rows = filtered) => {
    if (!rows.length) { toast.error('There are no clients to export'); return; }
    downloadCsv(`clients-${todayIso()}.csv`, csvRows(rows));
    toast.success(`Exported ${plural(rows.length, 'client')}`);
  };

  async function exportAll(e) {
    if (e) e.preventDefault();
    const pw = xlsx.password;
    if (pw.trim().length < 6) { setXlsx((s) => ({ ...s, error: 'Use at least 6 characters.' })); return; }
    setXlsx((s) => ({ ...s, busy: true, error: null }));
    try {
      const res = await api.get('/export/clients?format=xlsx', { responseType: 'blob', headers: { 'X-Export-Password': pw } });
      const disposition = res.headers?.['content-disposition'];
      const match = disposition && /filename="?([^"';]+)"?/i.exec(disposition);
      const name = (match && match[1]) || `clients-${todayIso()}.xlsx`;
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setXlsx({ open: false, password: '', error: null, busy: false });
      toast.success('Exported all clients to Excel. You’ll need the password to open the file.');
    } catch (err) {
      setXlsx((s) => ({ ...s, busy: false, error: err.response?.data?.message || 'The export failed. Try again.' }));
    }
  }

  const runDeteriorationScan = async () => {
    setScan({ open: true, loading: true, alerts: null });
    try {
      const { data } = await api.post('/ai/client-deterioration');
      setScan({ open: true, loading: false, alerts: data.alerts || [] });
    } catch (err) {
      toast.error(err.response?.data?.message || 'The scan failed. Try again.');
      setScan({ open: false, loading: false, alerts: null });
    }
  };

  const selectedRows = clients.filter((c) => selected.includes(c.id));
  const selEmails = [...new Set(selectedRows.map((c) => c.primary_contact_email).filter(Boolean))];
  const selPhones = [...new Set(selectedRows.map((c) => c.primary_contact_phone || c.mobile).filter(Boolean))];

  // ── Columns ────────────────────────────────────────────────────────────────
  const columns = [
    {
      key: 'name', header: 'Name', sortable: true,
      render: (c) => (
        <div className="flex items-center gap-2.5 min-w-0">
          <PersonAvatar id={c.id} photo={c.profile_photo_url} firstName={c.first_name} lastName={c.last_name} size="sm" />
          <div className="min-w-0">
            <Link to={`/clients/${c.id}`} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} className="font-semibold text-slate-800 hover:text-primary-700">
              {nameOf(c) || 'Unnamed client'}
            </Link>
            {riskScores[c.id] && riskScores[c.id].level !== 'green' && <div className="mt-0.5"><RiskBadge rs={riskScores[c.id]} /></div>}
          </div>
        </div>
      ),
    },
    {
      key: 'type', header: 'Type', sortable: true,
      render: (c) => (c.client_type ? <span className="badge-gray whitespace-nowrap">{clientTypeLabel(c.client_type)}</span> : <span className="text-slate-500">—</span>),
    },
    {
      key: 'ndis', header: 'NDIS number', sortable: true,
      render: (c) => (c.ndis_number ? <span className="text-sm text-slate-700 tabular-nums">{c.ndis_number}</span> : <span className="text-slate-500">—</span>),
    },
    {
      key: 'age', header: 'Age', sortable: true, align: 'right',
      render: (c) => { const a = ageOf(c.date_of_birth); return <span className="text-slate-600 tabular-nums">{a == null ? '—' : a}</span>; },
    },
    { key: 'review', header: 'Next review', sortable: true, render: (c) => <ReviewCell date={c.next_review_date} /> },
    { key: 'contact', header: 'Primary contact', sortable: true, render: (c) => <ContactCell c={c} /> },
    {
      key: 'location', header: 'Location', sortable: true,
      render: (c) => { const loc = [c.suburb, c.state].filter(Boolean).join(', '); return loc ? <span className="text-slate-600">{loc}</span> : <span className="text-slate-500">—</span>; },
    },
    {
      key: 'actions', header: <span className="sr-only">Actions</span>, width: 48, align: 'right',
      // Keys pressed on the menu button stay with it (Enter must open the menu, not the row).
      render: (c) => (
        <span className="inline-flex" onKeyDown={(e) => e.stopPropagation()}>
          <OverflowMenu items={rowMenu(c)} label={`Actions for ${nameOf(c) || 'client'}`} />
        </span>
      ),
    },
  ];

  const tableEmpty = filtersOn
    ? { icon: Users, title: 'No clients match', hint: 'Try a different search, or clear the filters.', action: <button type="button" onClick={clearFilters} className="btn-secondary">Clear filters</button> }
    : tab === 'archived'
      ? { icon: Users, title: 'No archived clients', hint: 'Clients you archive appear here, with all of their records kept.' }
      : { icon: Users, title: 'No clients yet', hint: 'Add your first participant to roster shifts, keep notes and bill their plan.', action: <Link to="/clients/new" className="btn-primary"><UserPlus size={15} aria-hidden /> Add client</Link> };

  const tabs = TABS.map((t) => ({
    ...t,
    count: t.id === 'active' ? (listState.loading ? undefined : activeClients.length)
      : t.id === 'archived' ? (listState.loading ? undefined : archivedClients.length)
        : t.id === 'teams' ? (teams.rows ? teams.rows.length : undefined)
          : (docs.loading ? undefined : docRows.length),
  }));

  const showTiles = !listState.error;

  return (
    <div className={PAGE_WIDE}>
      {/* Actions — the top bar already says "Clients" */}
      <div className="flex items-center justify-end gap-2">
        <OverflowMenu label="Export clients" icon={ExportTrigger}
          className="!px-3 !py-2 border border-slate-300 bg-white !text-slate-700 hover:!bg-slate-50 !rounded-xl"
          items={[
            { label: 'Visible rows (CSV)', icon: FileText, onClick: () => exportCsv() },
            { label: 'All clients, all fields (Excel)', icon: FileSpreadsheet, onClick: () => setXlsx({ open: true, password: '', error: null, busy: false }) },
          ]} />
        {connected.xero && (
          <Link to="/invoices?tab=xero&view=clients" className="btn-secondary" title="Create Xero contacts for clients who are not in Xero yet">
            <Send size={15} aria-hidden /><span className="hidden sm:inline">Send to Xero</span><span className="sm:hidden">Xero</span>
          </Link>
        )}
        {connected.quickbooks && (
          <Link to="/invoices?tab=quickbooks&view=clients" className="btn-secondary" title="Create QuickBooks customers for clients who are not in QuickBooks yet">
            <Send size={15} aria-hidden /><span className="hidden sm:inline">Send to QuickBooks</span><span className="sm:hidden">QuickBooks</span>
          </Link>
        )}
        <Link to="/clients/new" className="btn-primary">
          <UserPlus size={15} aria-hidden /><span className="hidden sm:inline">Add client</span><span className="sm:hidden">Add</span>
        </Link>
      </div>

      {/* One row of numbers; each filters the list */}
      {showTiles && (
        <div className="flex gap-3 overflow-x-auto snap-x pb-1 -mb-1 sm:grid sm:grid-cols-3 xl:grid-cols-5 sm:overflow-visible sm:pb-0 sm:mb-0">
          <FilterTile label="Reviews due" value={listState.loading ? '—' : reviewsDue}
            sub={reviewsOverdue ? `${reviewsOverdue} overdue` : 'Next 14 days'}
            tone={reviewsOverdue ? 'rose' : reviewsDue ? 'amber' : 'slate'}
            active={tab === 'active' && kpi === 'reviews'} onClick={() => pickKpi('reviews')} />
          <FilterTile label="Documents expiring" value={docs.loading ? '—' : docs.error ? '—' : docRows.length}
            sub={docsExpired ? `${docsExpired} already expired` : 'Next 30 days'}
            tone={docsExpired ? 'rose' : docRows.length ? 'amber' : 'slate'}
            active={tab === 'documents'} onClick={() => setTab(tab === 'documents' ? 'active' : 'documents')} />
          <FilterTile label="Fund issues" value={listState.loading || riskError ? '—' : fundIssues}
            sub="Low or expired funds" tone={fundIssues ? 'amber' : 'slate'}
            active={tab === 'active' && kpi === 'funds'} onClick={() => pickKpi('funds')} />
          <FilterTile label="No contact on file" value={listState.loading || riskError ? '—' : noContact}
            sub="No family or guardian listed" tone={noContact ? 'amber' : 'slate'}
            active={tab === 'active' && kpi === 'contacts'} onClick={() => pickKpi('contacts')} />
          {/* Invoices aren't a property of a participant, so this one opens Invoices instead of filtering. */}
          <FilterTile label="Unpaid invoices" value={unpaid == null ? '—' : unpaid}
            sub="Sent or overdue · opens Invoices" tone={unpaid ? 'amber' : 'slate'}
            onClick={() => navigate('/invoices')} />
        </div>
      )}

      <section className="bg-white border border-slate-200 rounded-xl" aria-label="Clients">
        <Tabs tabs={tabs} value={tab} onChange={setTab} ariaLabel="Client lists" className="px-2 sm:px-3" />

        {(tab === 'active' || tab === 'archived') && (
          <>
            <div className="flex flex-wrap items-center gap-2 px-3 sm:px-4 py-3 border-b border-slate-100">
              <SearchInput value={search} onChange={setSearch} placeholder="Search name, NDIS number or contact" className="w-full sm:w-72" />
              <label className="sr-only" htmlFor="client-type-filter">Type</label>
              <select id="client-type-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                className="h-8 rounded-lg border border-slate-200 bg-white pl-2.5 pr-8 text-[13px] text-slate-700 hover:border-slate-300 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100">
                <option value="all">All types</option>
                {typeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {tab === 'active' && kpi && (
                <button type="button" onClick={() => setKpi(null)}
                  className="inline-flex items-center gap-1.5 h-8 pl-2.5 pr-2 rounded-full border border-primary-300 bg-primary-50 text-[12px] font-medium text-primary-700 hover:bg-primary-100"
                  aria-label={`Remove filter: ${KPI_FILTERS[kpi].label}`}>
                  {KPI_FILTERS[kpi].label}<X size={13} aria-hidden />
                </button>
              )}
              <div className="ml-auto flex items-center gap-2">
                {tab === 'active' && (
                  <button type="button" onClick={runDeteriorationScan} disabled={scan.loading} className="btn-secondary btn-sm !h-8">
                    {scan.loading ? <Loader2 size={13} className="animate-spin" aria-hidden /> : <Sparkles size={13} className="text-violet-500" aria-hidden />}
                    AI scan
                  </button>
                )}
                <button type="button" onClick={() => setDense((d) => !d)} aria-pressed={dense}
                  className="hidden sm:inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-[12px] font-medium text-slate-600 hover:bg-slate-100">
                  <Rows size={14} aria-hidden />{dense ? 'Comfortable rows' : 'Compact rows'}
                </button>
              </div>
            </div>

            {tab === 'archived' && listState.archivedError ? (
              <ErrorState message={listState.archivedError} onRetry={loadClients} />
            ) : (
              <>
                {/* Wide screens: the table. Phones: one card per client. */}
                <div className="hidden sm:block">
                  <DataTable
                    columns={columns} rows={pageRows} rowKey="id"
                    loading={listState.loading} error={listState.error} onRetry={loadClients} empty={tableEmpty}
                    sort={sort} onSortChange={setSort}
                    selectable selected={selected} onSelectedChange={setSelected}
                    onRowClick={(c) => navigate(`/clients/${c.id}`)}
                    dense={dense} maxHeight="68vh" label={tab === 'archived' ? 'Archived clients' : 'Active clients'}
                  />
                </div>
                <div className="sm:hidden">
                  {listState.loading ? <TableSkeleton rows={5} cols={3} />
                    : listState.error ? <ErrorState message={listState.error} onRetry={loadClients} />
                      : pageRows.length === 0 ? <EmptyState {...tableEmpty} />
                        : (
                          <ul className="divide-y divide-slate-100">
                            {pageRows.map((c) => (
                              <li key={c.id} className="flex items-center gap-3 px-3 py-3">
                                <Link to={`/clients/${c.id}`} className="flex items-center gap-3 min-w-0 flex-1">
                                  <PersonAvatar id={c.id} photo={c.profile_photo_url} firstName={c.first_name} lastName={c.last_name} size="md" />
                                  <span className="min-w-0">
                                    <span className="flex items-center gap-1.5 flex-wrap">
                                      <span className="font-semibold text-slate-800">{nameOf(c) || 'Unnamed client'}</span>
                                      <RiskBadge rs={riskScores[c.id]} />
                                    </span>
                                    <span className="block text-[12.5px] text-slate-500 mt-0.5">
                                      {[c.client_type ? clientTypeLabel(c.client_type) : null, c.next_review_date ? `Review ${fmtDate(dayOf(c.next_review_date))}` : null].filter(Boolean).join(' · ') || '—'}
                                    </span>
                                  </span>
                                </Link>
                                <OverflowMenu items={rowMenu(c)} label={`Actions for ${nameOf(c) || 'client'}`} className="!p-2.5" />
                              </li>
                            ))}
                          </ul>
                        )}
                </div>
              </>
            )}

            {!listState.loading && !listState.error && filtered.length > 0 && (
              <div className="flex flex-wrap items-center gap-3 px-3 sm:px-4 py-2.5 border-t border-slate-100">
                <span className="text-[12.5px] text-slate-500 tabular-nums">{num(from)}–{num(to)} of {num(filtered.length)}</span>
                {pages > 1 && (
                  <div className="ml-auto flex items-center gap-1">
                    <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1}
                      aria-label="Previous page" className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40">
                      <ChevronLeft size={15} aria-hidden />
                    </button>
                    <span className="px-2 text-[12.5px] text-slate-600 tabular-nums">Page {safePage} of {pages}</span>
                    <button type="button" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={safePage === pages}
                      aria-label="Next page" className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40">
                      <ChevronRight size={15} aria-hidden />
                    </button>
                  </div>
                )}
              </div>
            )}

            <BulkBar count={selected.length} onClear={() => setSelected([])} noun="selected">
              {selEmails.length > 0 && (
                <a href={`mailto:?bcc=${selEmails.join(',')}`} className="btn-secondary btn-sm"><Mail size={13} aria-hidden /> Email contacts</a>
              )}
              {selPhones.length > 0 && (
                <a href={`sms:${selPhones.join(',')}`} className="btn-secondary btn-sm"><MessageSquare size={13} aria-hidden /> SMS contacts</a>
              )}
              <button type="button" onClick={() => exportCsv(selectedRows)} className="btn-secondary btn-sm"><Download size={13} aria-hidden /> Export CSV</button>
            </BulkBar>
          </>
        )}

        {tab === 'teams' && (
          <div className="p-4">
            {teams.loading ? <TableSkeleton rows={3} cols={3} />
              : teams.error ? <ErrorState message={teams.error} onRetry={loadTeams} />
                : !teams.rows || teams.rows.length === 0 ? (
                  <EmptyState icon={UsersRound} title="No teams yet"
                    hint="Teams group workers and participants for rostering. Set them up on the Teams page."
                    action={<Link to="/teams" className="btn-secondary">Open Teams</Link>} />
                ) : (
                  <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {teams.rows.map((t) => (
                      <li key={t.id}>
                        <Link to="/teams" className="block h-full rounded-xl border border-slate-200 p-4 hover:border-slate-300 transition-colors duration-150">
                          <span className="block font-semibold text-slate-800 text-sm">{t.name}</span>
                          {t.description && <span className="block text-[13px] text-slate-500 mt-0.5 line-clamp-2">{t.description}</span>}
                          <span className="flex items-center gap-3 mt-3 text-[12.5px] text-slate-600 tabular-nums">
                            {/* The teams API counts staff and clients separately (staff_count, client_count);
                                this read a member_count it never sends, so every card said "0 members". */}
                            <span>{plural(Number(t.staff_count) || 0, 'staff member')}</span>
                            <span>{plural(Number(t.client_count) || 0, 'client')}</span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
          </div>
        )}

        {tab === 'documents' && (
          <DataTable
            rows={docRows} rowKey="id" loading={docs.loading} error={docs.error} onRetry={loadDocs}
            label="Expired and expiring documents" maxHeight="68vh"
            empty={{ icon: FileWarning, title: 'Nothing expiring in the next 30 days', hint: 'Expiry dates come from each client’s Documents tab.' }}
            onRowClick={(d) => navigate(`/clients/${d.client_id}/documents`)}
            columns={[
              { key: 'client_name', header: 'Client', sortable: true, render: (d) => <span className="font-medium text-slate-800">{d.client_name || '—'}</span> },
              { key: 'name', header: 'Document', sortable: true, render: (d) => <span className="text-slate-700">{d.name || '—'}</span> },
              { key: 'expiry_date', header: 'Expires', sortable: true, render: (d) => <span className="tabular-nums text-slate-600 whitespace-nowrap">{fmtDate(d.expiry_date)}</span> },
              {
                key: 'status', header: 'Status', sortable: true, sortValue: (d) => daysUntil(d.expiry_date),
                render: (d) => {
                  const n = daysUntil(d.expiry_date);
                  if (n == null) return null;
                  return n < 0
                    ? <span className="badge-red whitespace-nowrap">Expired {plural(-n, 'day')} ago</span>
                    : <span className="badge-yellow whitespace-nowrap">{n === 0 ? 'Expires today' : `Expires in ${plural(n, 'day')}`}</span>;
                },
              },
              {
                key: 'view', header: <span className="sr-only">Open</span>, align: 'right',
                render: (d) => (
                  <Link to={`/clients/${d.client_id}/documents`} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 text-[13px] font-medium text-primary-700 hover:underline whitespace-nowrap">
                    View <ChevronRight size={13} aria-hidden />
                  </Link>
                ),
              },
            ]}
          />
        )}
      </section>

      {/* Password-protected Excel export (replaces the browser prompt) */}
      <Dialog open={xlsx.open} onClose={() => !xlsx.busy && setXlsx((s) => ({ ...s, open: false }))} size="sm"
        title="Export all clients to Excel"
        description="Every field for every client, in one workbook. The file is locked with the password you set."
        footer={<>
          <button type="button" onClick={() => setXlsx((s) => ({ ...s, open: false }))} className="btn-secondary" disabled={xlsx.busy}>Cancel</button>
          <button type="submit" form="xlsx-export-form" className="btn-primary" disabled={xlsx.busy}>
            {xlsx.busy ? <><Loader2 size={14} className="animate-spin" aria-hidden /> Exporting…</> : 'Export'}
          </button>
        </>}>
        <form id="xlsx-export-form" onSubmit={exportAll}>
          <Field label="File password" required error={xlsx.error} hint="At least 6 characters. You’ll need it to open the file.">
            {(fid) => (
              <input id={fid} type="password" autoComplete="new-password" className="input-field" value={xlsx.password}
                onChange={(e) => setXlsx((s) => ({ ...s, password: e.target.value, error: null }))} />
            )}
          </Field>
        </form>
      </Dialog>

      {/* AI deterioration scan */}
      <Dialog open={scan.open} onClose={() => setScan({ open: false, loading: false, alerts: null })} size="lg"
        title="Client deterioration scan"
        description="Looks at incident trends, missed shifts, medication changes and support hours.">
        {scan.loading && (
          <div className="flex items-center gap-2 py-10 justify-center text-slate-500">
            <Loader2 size={18} className="animate-spin" aria-hidden /><span className="text-sm">Scanning client health indicators…</span>
          </div>
        )}
        {!scan.loading && scan.alerts && scan.alerts.length === 0 && (
          <EmptyState title="No deterioration signals found" hint="All active clients appear stable." />
        )}
        {!scan.loading && scan.alerts && scan.alerts.length > 0 && (
          <ul className="space-y-3">
            {scan.alerts.map((alert, i) => (
              <li key={i} className="border border-slate-200 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    {alert.clientId ? (
                      <Link to={`/clients/${alert.clientId}`} onClick={() => setScan({ open: false, loading: false, alerts: null })}
                        className="font-semibold text-slate-800 text-sm hover:text-primary-700 hover:underline">{alert.clientName}</Link>
                    ) : (
                      <span className="font-semibold text-slate-800 text-sm">{alert.clientName}</span>
                    )}
                    <p className="text-[13px] text-slate-600 mt-0.5">{alert.summary}</p>
                  </div>
                  <span className={clsx('shrink-0', alert.alertLevel === 'urgent' ? 'badge-red' : 'badge-yellow')}>{alert.alertLevel === 'urgent' ? 'Urgent' : 'Watch'}</span>
                </div>
                {alert.signals?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">{alert.signals.map((sig, si) => <span key={si} className="badge-gray">{sig}</span>)}</div>
                )}
                {alert.recommendedAction && (
                  <p className="text-[13px] text-slate-700 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                    <span className="font-medium">Suggested action: </span>{alert.recommendedAction}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Dialog>
    </div>
  );
}
